import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  booleanAttribute,
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  runInInjectionContext,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import {
  CommonMediaQueries,
  DropdownModule,
  DropdownTriggerForDirective,
  IconModule,
  MediaScreenService,
  SimpleInputComponent,
  SimpleInputModule,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';

import {
  Account,
  ACCOUNT_GROUP_TYPE,
  AccountType,
  DEFAULT_CURRENCY,
} from '@iw/core/entities';
import { limitIntegerDigits, stringToInteger } from '@iw/core/helpers';
import { IwDialogService } from '@iw/core/services';

import {
  AccountWithAmountItem,
  WalletWithAmountValue,
} from './account-with-amount-select.entities';
import {
  AccountItemComponent,
  CurrencyItemComponent,
  UserItemComponent,
} from './components';
import { AccountsDialogComponent } from './components/accounts-dialog';
import { AccountsDialogData } from './components/accounts-dialog/accounts-dialog.entities';

@Component({
  selector: 'iw-account-with-amount-select',
  standalone: true,
  imports: [
    DropdownModule,
    ReactiveFormsModule,
    SimpleInputModule,
    IconModule,
    TranslateModule,
    AsyncPipe,
    AccountItemComponent,
    UserItemComponent,
    CurrencyItemComponent,
  ],
  templateUrl: './account-with-amount-select.component.html',
  styleUrl: './account-with-amount-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccountWithAmountSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AccountWithAmountSelectComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('slideFromTop', [
      transition(':enter', [
        style({ transform: 'translateY(-16px)', opacity: 0 }),
        animate(
          '220ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '160ms ease-in',
          style({ transform: 'translateY(-16px)', opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class AccountWithAmountSelectComponent
  implements OnInit, AfterViewInit, ControlValueAccessor, Validator
{
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #translate = inject(TranslateService);
  readonly #dialogService = inject(IwDialogService);
  readonly #mediaScreenService = inject(MediaScreenService);

  readonly placeholder = input<string>('accountWithAmount.placeholder');
  readonly accounts = input<AccountWithAmountItem[]>();
  readonly withAmount = input<boolean>(true);
  readonly skipTouch = input<boolean>(false);
  readonly shouldPreselect = input<boolean>(false);
  readonly withSearch = input(false, { transform: booleanAttribute });
  readonly listLabel = input<string>(null);
  readonly searchPlaceholder = input<string>(
    'accountWithAmount.search.placeholder',
  );
  readonly isAccountSender = input<boolean>(false);
  readonly disabledAccountId = input<string | null>(null);
  readonly defaultCurrency = input<string | null>(DEFAULT_CURRENCY);
  readonly hostAmountError = input<string | null>(null);

  readonly amountInput = viewChild<SimpleInputComponent>('amountInput');
  readonly dropdownTrigger =
    viewChild<DropdownTriggerForDirective>('dropdownRef');

  isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  readonly searchTrigger = output<string>();
  readonly focused = output<void>();
  readonly amountFocusOut = output<void>();

  amountCtrlSignal: Signal<string>;

  amountChangeListener = effect(() => {
    if (!this.amountCtrl) return;

    const amountCtrlValue = this.amountCtrlSignal();
    const amountInput = this.amountInput();

    if (amountCtrlValue && amountInput?.inputElement) {
      const maxLimitDigits = limitIntegerDigits(amountCtrlValue);

      if (maxLimitDigits !== amountCtrlValue) {
        this.amountCtrl.setValue(maxLimitDigits, { emitEvent: false });
      } else {
        this.#handleAmountInputWidthByAmountValue(amountCtrlValue);
      }
    }
  });

  form: FormGroup;
  isDisabled = false;

  get errorDictionary(): Record<string, string> {
    return {
      required: 'common.validationMessages.required',
      notInteger: 'common.validationMessages.notInteger',
      insufficientFunds: 'common.validationMessages.insufficientFunds',
      minAmount: this.#translate.instant(
        'common.validationMessages.minAmount',
        { value: this.accountCtrl.value?.currency?.minAmount },
      ),
    };
  }

  get accountCtrl(): AbstractControl<AccountWithAmountItem> {
    return this.form.get('account');
  }

  get amountCtrl(): AbstractControl<string> {
    return this.form.get('amount');
  }

  get searchCtrl(): AbstractControl<string> {
    return this.form.get('search');
  }

  get amountError(): string | null {
    const hostError = this.hostAmountError();
    if (hostError) {
      return hostError;
    }
    const ctrl = this.amountCtrl;
    if (
      !ctrl ||
      !(ctrl.invalid && (ctrl.touched || this.skipTouch() || ctrl.dirty))
    )
      return null;
    const errorKeys = Object.keys(ctrl.errors);
    const errorDictionary = this.errorDictionary;

    const errorKey = errorKeys.find((item) => errorDictionary[item]);

    return errorDictionary[errorKey] || errorKeys[0];
  }

  get accountError(): string | null {
    const ctrl = this.accountCtrl;
    if (
      !ctrl ||
      !(ctrl.invalid && (ctrl.touched || this.skipTouch() || ctrl.dirty))
    ) {
      return null;
    }
    const errorKeys = Object.keys(this.accountCtrl.errors);
    const errorDictionary = this.errorDictionary;

    const errorKey = errorKeys.find((item) => errorDictionary[item]);

    return errorDictionary[errorKey] || errorKeys[0];
  }

  onChange: (val: WalletWithAmountValue) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit() {
    this.#initForm();
    this.#handleSearch();
    this.#addAmountValidators();
  }

  ngAfterViewInit() {
    if (this.shouldPreselect()) {
      this.#initAccountSelection();
    }
  }

  // FORM CONTROL

  writeValue(val: WalletWithAmountValue | null): void {
    this.form.patchValue(val, { emitEvent: false });
  }

  registerOnChange(fn: (val: WalletWithAmountValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.form.valid) return null;

    const accountErrors = this.accountCtrl?.errors ?? null;
    const amountErrors = this.amountCtrl?.errors ?? null;

    return {
      ...(accountErrors ? { account: accountErrors } : {}),
      ...(amountErrors ? { amount: amountErrors } : {}),
    };
  }

  // CUSTOM LOGIC

  focusInput(): void {
    if (Number(this.amountCtrl.value) === 0) {
      this.amountCtrl.setValue('', { emitEvent: false });
    }
    this.focused.emit();
  }

  isItemDisabled = (acc: Account) =>
    !!this.disabledAccountId() && this.disabledAccountId() === acc.id;

  selectAccount(account: Account): void {
    if (this.isItemDisabled(account)) return;

    if (account.id === this.accountCtrl.value?.id) {
      if (this.withAmount() && this.amountError) {
        this.#focusAmountInput();
      }
      return;
    }

    this.accountCtrl.setValue(account);
    this.#restartAmountCtrl();
    if (this.withAmount()) {
      this.#focusAmountInput();
    }
  }

  getAccountType(account: AccountWithAmountItem) {
    return account.type === ACCOUNT_GROUP_TYPE.SALARY
      ? AccountType.TreasurerWallet
      : AccountType.FiatWallet;
  }

  normalizeOnFocusOut(event: Event): void {
    //'0.0' -> '0' this is an edge case which is not expected on the default mask behavior, will be moved to the custom directive later
    const input = event.target as HTMLInputElement;
    if (input.value === '0.0' || input.value === '0.00') {
      this.amountCtrl.setValue('0', { emitEvent: false });
    }

    if (input.value && input.value.endsWith('.')) {
      this.amountCtrl.setValue(input.value.slice(0, -1), { emitEvent: false });
    }

    this.amountFocusOut.emit();
  }

  openDropdown() {
    if (!this.isDesktop()) {
      return this.#openAccountsSelectDialog();
    }
    const dropdownTrigger = this.dropdownTrigger();
    dropdownTrigger.openDropdown.call(dropdownTrigger);
  }

  #handleSearch(): void {
    if (!this.withSearch()) {
      return;
    }

    this.searchCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged(),
        debounceTime(300),
      )
      .subscribe((value) => {
        this.searchTrigger.emit(value?.toString());
      });
  }

  #initForm(): void {
    this.form = this.#fb.group({
      account: [null, Validators.required],
      amount: [null, this.withAmount() ? Validators.required : null],
      search: [null],
    });

    runInInjectionContext(this.#injector, () => {
      this.amountCtrlSignal = toSignal(this.amountCtrl.valueChanges);
    });

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map(({ account, amount }) => ({ account, amount })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.amount === curr.amount &&
            prev.account?.id === curr.account?.id,
        ),
      )
      .subscribe((v) => this.onChange(v));
  }

  #restartAmountCtrl(): void {
    this.amountCtrl.setValue(null, { emitEvent: false });
    this.amountCtrl.clearValidators();
    this.#addAmountValidators();
    this.amountCtrl.updateValueAndValidity({ emitEvent: true });
  }

  #initAccountSelection() {
    const accounts = this.accounts();

    if (this.accountCtrl.value) {
      if (this.withAmount()) {
        this.#focusAmountInput();
      }
      return;
    }
    if (accounts?.length === 1) {
      return this.selectAccount(accounts[0]);
    }

    this.openDropdown();
  }

  #focusAmountInput(): void {
    // NOTE focus after the view updates and the input exists
    afterNextRender(
      () => {
        this.amountInput()?.inputElement?.nativeElement?.focus();
      },
      { injector: this.#injector },
    );
  }

  #addAmountValidators(): void {
    if (!this.withAmount()) return;

    const amountValidators = [
      Validators.required,
      this.#minAmountValidator(),
      this.#correctIntegerValidator(),
    ];

    if (this.isAccountSender()) {
      amountValidators.push(this.#insufficientFundsValidator());
    }
    this.amountCtrl.addValidators(amountValidators);
  }

  #handleAmountInputWidthByAmountValue(value: string): void {
    const amountElement = this.amountInput().inputElement;
    if (!amountElement || !amountElement.nativeElement) return;

    const avgCharWidth = 11;
    const minWidth = 12;
    const padding = 2;

    const length = value ? value.length : 0;

    const width = Math.max(minWidth, length * avgCharWidth + padding);

    amountElement.nativeElement.style.width = `${width}px`;
  }

  #correctIntegerValidator(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const value = control.value;
      if (value && isNaN(stringToInteger(value))) {
        return { notInteger: true };
      }

      return null;
    };
  }

  #insufficientFundsValidator(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const value = control.value;
      const balance = this.accountCtrl.value?.balance;
      if (value && balance && stringToInteger(value) > +balance) {
        return { insufficientFunds: true };
      }

      return null;
    };
  }

  #minAmountValidator(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const value = control.value;
      const minAmount = this.accountCtrl.value?.currency?.minAmount;
      if (value && minAmount && stringToInteger(value) < +minAmount) {
        return { minAmount: true };
      }

      return null;
    };
  }

  #openAccountsSelectDialog(): void {
    this.#dialogService
      .open<AccountsDialogComponent, AccountsDialogData>(
        {
          components: [AccountsDialogComponent],
          title: this.#translate.instant(this.placeholder()),
          isOnTop: true,
          fullScreen: true,
          data: {
            form: this.form,
            accounts: this.accounts,
            listLabel: this.listLabel(),
            withSearch: this.withSearch(),
            defaultCurrency: this.defaultCurrency(),
            disabledAccountId: this.disabledAccountId(),
            searchPlaceholder: this.searchPlaceholder(),
          },
        },
        this.#injector,
      )
      .afterClosed<Account>()
      .pipe(filter(Boolean))
      .subscribe((value) => {
        this.selectAccount(value);
      });
  }
}
